import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ShareIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChartBarIcon,
  TableCellsIcon,
  CubeIcon,
  BeakerIcon,
  DocumentIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import {
  ExclamationTriangleIcon as ExclamationTriangleSolid
} from '@heroicons/react/24/solid';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { useBilling } from '../../../contexts/BillingContext';
import { getSession } from '../../../api/supabase';
import { apiRequest } from '../../../utils/apiUtils';

const TableRelationships = ({ tableName, connectionId }) => {
  // Check if lineage viewing is enabled for this plan
  // Note: lineage_enabled controls both VIEW access and automated Tier 5 collection (Starter+)
  const { isFeatureEnabled, plan, getUpgradeUrl } = useBilling();
  const lineageEnabled = isFeatureEnabled('lineage_enabled');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lineageData, setLineageData] = useState({
    upstream: [],
    downstream: [],
    table_info: null,
    total_dependencies: 0
  });

  useEffect(() => {
    const fetchLineageData = async () => {
      if (!connectionId || !tableName) return;

      // Don't fetch if lineage is not enabled for this plan
      if (!lineageEnabled) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const data = await apiRequest(`/lineage/table/${encodeURIComponent(tableName)}?connection_id=${connectionId}`, {
          method: 'GET'
        });

        setLineageData(data);
      } catch (error) {
        console.error('Error fetching lineage data:', error);
        setError('Failed to load lineage data');
      } finally {
        setLoading(false);
      }
    };

    fetchLineageData();
  }, [connectionId, tableName, lineageEnabled]);

  const getObjectIcon = (objectType) => {
    switch (objectType?.toLowerCase()) {
      case 'table':
        return TableCellsIcon;
      case 'view':
        return CubeIcon;
      case 'materialized_view':
        return BeakerIcon;
      default:
        return DocumentIcon;
    }
  };

  const getCriticalityColor = (criticality) => {
    switch (criticality?.toLowerCase()) {
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low':
        return 'text-green-600 bg-green-50 border-green-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <LoadingSpinner size="lg" className="mx-auto" />
        <p className="mt-4 text-sm text-gray-500">Loading lineage data for {tableName}...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading lineage</h3>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  const { upstream, downstream, table_info, total_dependencies } = lineageData;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Data Lineage & Dependencies</h2>
        <p className="text-gray-600">
          Understand how {tableName} connects to other data sources and where it's used
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                <ArrowLeftIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Upstream Sources</dt>
                  <dd className="text-2xl font-semibold text-gray-900">{upstream.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                <ArrowRightIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Downstream Usage</dt>
                  <dd className="text-2xl font-semibold text-gray-900">{downstream.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-100 rounded-md p-3">
                <ShareIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Dependencies</dt>
                  <dd className="text-2xl font-semibold text-gray-900">{total_dependencies}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lineage Visualization */}
      {total_dependencies > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Upstream Dependencies */}
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Upstream Sources</h3>
              <ArrowLeftIcon className="h-5 w-5 text-gray-400" />
            </div>
            
            {upstream.length > 0 ? (
              <div className="space-y-4">
                {upstream.map((obj, index) => {
                  const IconComponent = getObjectIcon(obj.object_type);
                  return (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 bg-blue-100 rounded-md p-2">
                            <IconComponent className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900">{obj.object_name}</h4>
                            <p className="text-xs text-gray-500">{obj.full_name}</p>
                            <div className="mt-2 flex items-center space-x-2">
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                {obj.object_type}
                              </span>
                              {obj.business_criticality && obj.business_criticality !== 'unknown' && (
                                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getCriticalityColor(obj.business_criticality)}`}>
                                  {obj.business_criticality} priority
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {obj.relationship_type && (
                        <div className="mt-3 text-xs text-gray-500">
                          <span className="font-medium">Relationship:</span> {obj.relationship_type}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-500 mb-2">No upstream dependencies</div>
                <div className="text-sm text-gray-400">
                  This table doesn't depend on other tables
                </div>
              </div>
            )}
          </div>

          {/* Downstream Usage */}
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Downstream Usage</h3>
              <ArrowRightIcon className="h-5 w-5 text-gray-400" />
            </div>
            
            {downstream.length > 0 ? (
              <div className="space-y-4">
                {downstream.map((obj, index) => {
                  const IconComponent = getObjectIcon(obj.object_type);
                  return (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 bg-green-100 rounded-md p-2">
                            <IconComponent className="h-5 w-5 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900">{obj.object_name}</h4>
                            <p className="text-xs text-gray-500">{obj.full_name}</p>
                            <div className="mt-2 flex items-center space-x-2">
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                {obj.object_type}
                              </span>
                              {obj.business_criticality && obj.business_criticality !== 'unknown' && (
                                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getCriticalityColor(obj.business_criticality)}`}>
                                  {obj.business_criticality} priority
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {obj.relationship_type && (
                        <div className="mt-3 text-xs text-gray-500">
                          <span className="font-medium">Relationship:</span> {obj.relationship_type}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-500 mb-2">No downstream usage</div>
                <div className="text-sm text-gray-400">
                  No other tables or views use this table
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <div className="text-center py-16">
            <div className={`mx-auto w-24 h-24 rounded-full flex items-center justify-center mb-6 ${
              lineageEnabled ? 'bg-gradient-to-br from-blue-100 to-green-100' : 'bg-amber-100'
            }`}>
              {lineageEnabled ? (
                <ShareIcon className="h-12 w-12 text-blue-600" />
              ) : (
                <LockClosedIcon className="h-12 w-12 text-amber-600" />
              )}
            </div>

            {lineageEnabled ? (
              <>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No lineage data available</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  {lineageData.message || "Lineage discovery hasn't been run for this table yet."}
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-lg mx-auto">
                  <div className="flex items-start">
                    <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-1 mr-3" />
                    <div className="flex-1 text-left">
                      <h4 className="text-sm font-medium text-blue-800 mb-1">Enable Lineage Discovery</h4>
                      <p className="text-sm text-blue-700">
                        Run lineage discovery on your connection to see how tables relate to each other.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-amber-800 mb-2">Data Lineage Available on Starter Plan</h3>
                <p className="text-amber-700 mb-6 max-w-md mx-auto">
                  Upgrade to visualize how your tables connect to each other with upstream and downstream dependency tracking.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-lg mx-auto">
                  <div className="flex items-start">
                    <LockClosedIcon className="h-5 w-5 text-amber-600 mt-1 mr-3" />
                    <div className="flex-1 text-left">
                      <h4 className="text-sm font-medium text-amber-800 mb-1">Unlock Data Lineage</h4>
                      <p className="text-sm text-amber-700 mb-3">
                        See how issues cascade across your data pipeline with upstream and downstream dependency tracking.
                      </p>
                      <Link
                        to={getUpgradeUrl ? getUpgradeUrl() : '/settings/billing'}
                        className="inline-flex items-center px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700"
                      >
                        <LockClosedIcon className="h-4 w-4 mr-1.5" />
                        Upgrade to Starter
                      </Link>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TableRelationships;